/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
// @mui
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Scrollbar from 'src/components/scrollbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import Stack from '@mui/system/Stack';
import { useAuthContext } from '../../auth/hooks';
import { useDataContext } from '../../components/data/context';
import { getCompanyByUser } from '../../services/companies';
import FilterName from '../../components/dataTable/elements/filterName';
import Company from '../../models/company';
// ----------------------------------------------------------------------
const getCompaniesList = (userId: string, filter: any, order: string) =>
  getCompanyByUser(userId, filter);

const RECENT_PROJECTS_KEY = (userId: string) => `recentProjects-${userId}`;
const SELECTED_COMPANY_KEY = (userId: string) => `selectedCompany-${userId}`;

function getRecentProjects(userId: string): string[] {
  const data = localStorage.getItem(RECENT_PROJECTS_KEY(userId));
  return data ? JSON.parse(data) : [];
}

function addRecentProject(userId: string, companyId: string) {
  let recent = getRecentProjects(userId);
  recent = [companyId, ...recent.filter((id) => id !== companyId)].slice(0, 5);
  localStorage.setItem(RECENT_PROJECTS_KEY(userId), JSON.stringify(recent));
}

export default function ProjectsPopover() {
  const popover = usePopover();
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuthContext();
  const { selectedCompany, updateSelectedCompanyState } = useDataContext();

  const [companiesList, setCompaniesList] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [recentCompanies, setRecentCompanies] = useState<Company[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [strSearch, setStrSearch] = useState<string>('');
  const urlParams = new URLSearchParams(window.location.search);

  const filter = useMemo(
    () => ({
      status: { inq: ['ACTIVE', 'BLOCKED'] },
    }),
    []
  );

  const sort = 'name ASC';

  const getCompaniesByString = useCallback(
    (id = strSearch) => {
      setLoading(true);
      getCompaniesList(
        user?.id || '',
        {
          name: id,
          ...filter,
        },
        sort
      ).then((res) => {
        // console.log(res);
        setCompaniesList(res);
        setLoading(false);
      });
    },
    [strSearch]
  );

  const changeSelectedCompany = useCallback(
    (cId: string) => {
      localStorage.setItem(SELECTED_COMPANY_KEY(user?.id), cId);
      addRecentProject(user?.id, cId);
      updateSelectedCompanyState(cId);
    },
    [selectedCompany, user]
  );

  // Carga inicial de compañías y cachea en memoria
  useEffect(() => {
    if (user) {
      setLoading(true);
      getCompaniesList(user.id, filter, sort).then((res) => {
        setCompaniesList(res);
        setShow(true);
        setLoading(false);
        // Inicializa recientes
        const recentIds = getRecentProjects(user.id);
        setRecentCompanies(res.filter((c) => recentIds.includes(c.id)));
        setFilteredCompanies(res);
      });
    }
  }, [user]);

  // Filtrado local, evita llamadas innecesarias
  useEffect(() => {
    if (strSearch.trim() === '') {
      setFilteredCompanies(companiesList);
    } else {
      const search = strSearch.toLowerCase();
      setFilteredCompanies(companiesList.filter((c) => c.name.toLowerCase().includes(search)));
    }
    // Actualiza recientes según filtro, solo los 5 últimos
    const recentIds = getRecentProjects(user?.id || '').slice(0, 5);
    setRecentCompanies(
      companiesList
        .filter(
          (c) =>
            recentIds.includes(c.id) &&
            (strSearch === '' || c.name.toLowerCase().includes(strSearch.toLowerCase()))
        )
        // Ordena según el orden en recentIds
        .sort((a, b) => recentIds.indexOf(a.id) - recentIds.indexOf(b.id))
    );
  }, [strSearch, companiesList, user]);

  useEffect(() => {
    if (user) {
      const selectedCompanyFromStorage = localStorage.getItem(`selectedCompany-${user?.id}`);
      const companyId = urlParams.get('project');
      if (companyId && user?.dataUser?.role === 'ADMIN') {
        changeSelectedCompany(companyId);
      } else if (companyId || user?.dataUser?.companyId) {
        const cId = selectedCompanyFromStorage || user?.dataUser?.companyId;
        if (!companyId || companyId !== cId) urlParams.set('project', cId);
        if (user?.dataUser?.role === 'ADMIN')
          window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
        changeSelectedCompany(cId);
      }
    }
  }, [user]);

  const noDataMsg = () => (
    <MenuItem sx={{ p: 1 }}>
      <ListItemText
        primary="No data"
        primaryTypographyProps={{
          typography: 'subtitle2',
        }}
      />
    </MenuItem>
  );

  return (
    <>
      {show && (
        <Button
          color={popover.open ? 'primary' : 'inherit'}
          onClick={popover.onOpen}
          sx={{
            ...(popover.open && {
              bgcolor: theme.palette.action.selected,
            }),
            borderRadius: 1,
            px: 2,
            fontWeight: 500,
            textTransform: 'none',
            typography: 'subtitle2',
            fontSize: '0.8rem',
            fontFamily: theme.typography.fontFamily,
          }}
          size="small"
          endIcon={<ArrowDropDownIcon />}
        >
          {selectedCompany}
        </Button>
      )}

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 350, p: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          p={1.5}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">{t('text.pageTitles.projects')}</Typography>
          <Chip label={companiesList.length} size="small" sx={{ borderRadius: 2 }} />
        </Stack>

        <FilterName
          size={320}
          filterString={strSearch}
          onFilters={(_, value) => setStrSearch(value)}
        />

        <Scrollbar sx={{ height: 320, mt: 1 }}>
          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!loading && recentCompanies.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  px: 1,
                  pt: 1,
                  mb: 0.5,
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <AccessTimeIcon fontSize="small" />
                {t('text.labels.recent')}
              </Typography>
              {recentCompanies.map((company) => (
                <MenuItem
                  key={company.id}
                  sx={{
                    p: 1,
                    bgcolor: company.id === selectedCompany ? 'action.selected' : undefined,
                  }}
                  onClick={() => {
                    changeSelectedCompany(company.id);
                    popover.onClose();
                    urlParams.set('project', company.id);
                    window.history.pushState(
                      {},
                      '',
                      `${window.location.pathname}?${urlParams.toString()}`
                    );
                    window.location.reload();
                  }}
                >
                  <ListItemText
                    primary={company.name}
                    primaryTypographyProps={{
                      typography: 'subtitle2',
                      color: company.id === selectedCompany ? 'primary' : 'text.primary',
                      textTransform: 'capitalize',
                    }}
                  />
                </MenuItem>
              ))}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 1 }} />
            </>
          )}

          {!loading && filteredCompanies.length === 0 && noDataMsg()}

          {!loading &&
            filteredCompanies
              .filter((c) => !recentCompanies.some((rc) => rc.id === c.id))
              .map((company) => (
                <MenuItem
                  key={company.id}
                  sx={{
                    p: 1,
                    bgcolor: company.id === selectedCompany ? 'action.selected' : undefined,
                  }}
                  onClick={() => {
                    changeSelectedCompany(company.id);
                    popover.onClose();
                    urlParams.set('project', company.id);
                    window.history.pushState(
                      {},
                      '',
                      `${window.location.pathname}?${urlParams.toString()}`
                    );
                    window.location.reload();
                  }}
                >
                  <ListItemText
                    primary={company.name}
                    primaryTypographyProps={{
                      typography: 'subtitle2',
                      color: company.id === selectedCompany ? 'primary' : 'text.primary',
                      textTransform: 'capitalize',
                    }}
                  />
                </MenuItem>
              ))}
        </Scrollbar>
      </CustomPopover>
    </>
  );
}
